import { ArrowRight, Sprout, GraduationCap, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { COMMUNITY_IMAGES } from "../data/content";
import { Reveal } from "./Reveal";

const BADGES = [
  { icon: GraduationCap, label: "SYEP & Interns" },
  { icon: Sprout, label: "Local Hiring" },
  { icon: Sparkles, label: "MWBE Certified" },
];

export const CommunityImpact = () => {
  return (
    <section
      id="community"
      data-testid="community-impact-section"
      className="relative py-10 sm:py-14 lg:py-16 bg-brand-charcoal grain overflow-hidden"
    >
      {/* Soft accent glow */}
      <div
        aria-hidden="true"
        className="absolute -top-24 -right-24 sm:-top-32 sm:-right-32 h-[280px] w-[280px] sm:h-[460px] sm:w-[460px] rounded-full bg-brand-teal/15 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-16 -left-16 sm:-bottom-24 sm:-left-24 h-[220px] w-[220px] sm:h-[360px] sm:w-[360px] rounded-full bg-brand-gold/10 blur-3xl"
      />

      <div className="relative max-w-4xl mx-auto px-5 sm:px-6 lg:px-10">
        {/* 1. Heading + intro paragraph */}
        <Reveal>
          <p className="text-[12px] font-bold uppercase tracking-[0.22em] text-brand-gold">
            Community Impact
          </p>
        </Reveal>
        <Reveal delay={1}>
          <h2 className="mt-3 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-[1.05]">
            Committed to
            <br />
            Community.
          </h2>
        </Reveal>
        <Reveal delay={2}>
          <p className="mt-5 text-[15px] lg:text-[16.5px] text-white/75 leading-relaxed max-w-2xl">
            We believe food service can create opportunity. Powered by{" "}
            <span className="text-white font-semibold">
              Burgers Tacos Wings &amp; Seafood (BTWNYC)
            </span>{" "}
            — our quick-service restaurant and ghost-kitchen foundation — Fresh
            Fast Foods LLC connects food, training, mentorship, and community
            impact. We&rsquo;re proud to support staff development, local hiring,
            SYEP students, interns, young workers, and culinary school graduates
            across Jamaica, Queens and the wider NYC area.
          </p>
        </Reveal>

        {/* 2. Three small horizontal badge tiles */}
        <Reveal delay={3}>
          <ul
            data-testid="community-badges"
            className="mt-7 sm:mt-9 grid grid-cols-3 gap-2 sm:gap-3"
          >
            {BADGES.map(({ icon: Icon, label }) => (
              <li
                key={label}
                className="rounded-xl sm:rounded-2xl bg-white/[0.05] border border-white/10 px-2 py-3 sm:py-4 text-center"
              >
                <Icon className="h-4 w-4 sm:h-5 sm:w-5 mx-auto text-brand-gold" />
                <p className="mt-1.5 text-[10px] sm:text-[11.5px] font-bold uppercase tracking-[0.06em] sm:tracking-wider text-white leading-tight">
                  {label}
                </p>
              </li>
            ))}
          </ul>
        </Reveal>

        {/* 3. Intern / community images collage */}
        <Reveal delay={3}>
          <div className="mt-8 sm:mt-12 grid grid-cols-12 gap-2.5 sm:gap-3">
            <div className="col-span-7 row-span-2 rounded-2xl sm:rounded-3xl overflow-hidden">
              <img
                src={COMMUNITY_IMAGES.primary}
                alt="Fresh Fast Foods staff and SYEP students at work"
                className="h-full w-full object-cover aspect-[3/4]"
                loading="lazy"
              />
            </div>
            <div className="col-span-5 rounded-2xl sm:rounded-3xl overflow-hidden">
              <img
                src={COMMUNITY_IMAGES.secondary}
                alt="BTWNYC kitchen team preparing fresh catering"
                className="h-full w-full object-cover aspect-square"
                loading="lazy"
              />
            </div>
            <div className="col-span-5 rounded-2xl sm:rounded-3xl overflow-hidden">
              <img
                src={COMMUNITY_IMAGES.tertiary}
                alt="Fresh Fast Foods community event setup"
                className="h-full w-full object-cover aspect-square"
                loading="lazy"
              />
            </div>
          </div>
        </Reveal>

        {/* 4. Quote */}
        <Reveal delay={4}>
          <blockquote className="mt-10 sm:mt-12 border-l-2 border-brand-gold pl-5 text-white/85 max-w-2xl">
            <p className="font-display text-[18px] sm:text-[22px] leading-snug font-medium tracking-tight">
              &ldquo;Quality food starts with quality people. We&rsquo;re proud
              to connect food service, training, mentorship, and community
              impact.&rdquo;
            </p>
            <footer className="mt-3 text-[11.5px] font-bold uppercase tracking-[0.16em] text-brand-gold">
              — Fresh Fast Foods LLC
            </footer>
          </blockquote>
        </Reveal>

        {/* 5. Partner With Us CTA */}
        <Reveal delay={5}>
          <div className="mt-8 sm:mt-10">
            <Link
              to="/partner"
              data-testid="community-cta-btn"
              className="inline-flex items-center gap-2 rounded-full bg-brand-gold text-brand-charcoal px-6 py-3.5 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-gold-dark transition-all hover:scale-[1.02]"
            >
              Partner With Us
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default CommunityImpact;
